// fetchWithAuth.js
import { getValidSession, explicitRefresh } from './authSession';
import { authenticationService } from './';
import { history } from '../_helpers';

const DEFAULTS = {
  tokenType: 'id',          // 'id' or 'access'
  authScheme: 'raw',        // 'raw' or 'Bearer'
  retryOn401: true,
  // new:
  timeoutMs: 15000,         // client-side timeout per attempt
  maxRetries: 2,            // number of retries for transient errors
  baseDelayMs: 300,         // backoff base
};

const TRANSIENT_STATUSES = new Set([429, 500, 502, 503, 504]);

function sleep(ms) {
  return new Promise(r => setTimeout(r, ms));
}

function withTimeout(fetchPromise, timeoutMs, controller) {
  const t = setTimeout(() => controller.abort(), timeoutMs);
  return fetchPromise.finally(() => clearTimeout(t));
}

export async function fetchWithAuth(input, init = {}, opts = {}) {
  const cfg = { ...DEFAULTS, ...opts };

  // 🚨 Check if a user exists before making the API call
  if (!authenticationService.currentUserValue) {
    console.warn('[fetchWithAuth] No current user, redirecting to login.');
    // Redirect to login page
    const { pathname, search, hash } = history.location; // snapshot now
    // avoid pushing repeatedly if already on login
    if (pathname === '/authentication/login') return;

    history.push('/authentication/login', {
      from: { pathname, search, hash }, // plain POJO, not live location
    });

    // Return an empty Response so callers don't crash
    return new Response('', { status: 401, statusText: 'Unauthenticated' });
  }

  const doOnce = async () => {
    const session = await getValidSession({ allowNull: true });
    if (!session) return new Response('', { status: 401, statusText: 'Unauthenticated' });

    const token = cfg.tokenType === 'access'
      ? session.getAccessToken().getJwtToken()
      : session.getIdToken().getJwtToken();

    const headers = new Headers(init.headers || {});
    if (cfg.authScheme === 'Bearer') headers.set('Authorization', `Bearer ${token}`);
    else headers.set('Authorization', token);

    const controller = new AbortController();
    const reqInit = { ...init, headers, signal: controller.signal };

    return withTimeout(fetch(input, reqInit), cfg.timeoutMs, controller);
  };

  // First attempt
  let res;
  try {
    res = await doOnce();
  } catch (e) {
    // Network/abort counts as transient; turn into a 504-like Response so we can retry uniformly
    res = new Response('', { status: 504, statusText: (e?.name || 'NetworkError') });
  }

  // Auth-specific retry: 401 -> explicit refresh -> retry once
  if (res.status === 401 && cfg.retryOn401) {
    try {
      await explicitRefresh();
      res = await doOnce();
    } catch {
      // leave as is
    }
  }

  // Transient-retry loop (502/503/504/500/429)
  let attempt = 0;
  while (TRANSIENT_STATUSES.has(res.status) && attempt < cfg.maxRetries) {
    attempt += 1;
    const jitter = Math.random() * cfg.baseDelayMs;
    const delay = cfg.baseDelayMs * 2 ** (attempt - 1) + jitter;
    await sleep(delay);
    try {
      res = await doOnce();
    } catch {
      res = new Response('', { status: 504, statusText: 'NetworkError' });
    }
  }

  return res;
}
